import { type AssetExposure, baseAssetOf, isOptionSymbol } from '@/features/home/lib/home-exposure'
import type {
  OpportunityCategory,
  RelevanceReason,
  SetupId,
} from '@/features/home/lib/home-opportunities'
import type { PositionRisk } from '@/features/home/lib/home-risk'
import { expiryOfSymbol } from '@/features/home/lib/home-upcoming'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import { Exchange } from '@/types/generated/common/types'

/**
 * Reactive ideas for the user's book (the first of Alex and John's two buckets: something has
 * changed, adjust what you hold): cut a position that a normal move could liquidate, bank part
 * of a big winner, put a stop under a loser, close or roll an option about to expire, hedge a
 * concentrated book, stop paying heavy funding. Each comes with the order it would take —
 * written for the Order Form's prompt bar, where the user reviews it; nothing is ever sent
 * from Home. The AI adds its reasoning per idea in the daily brief. The other bucket, new
 * positions, is in home-opportunities.ts. Pure (tested in __tests__/home-ideas.test.ts).
 */

export type ReactiveKind =
  | 'reduce'
  | 'takeProfit'
  | 'stopLoss'
  | 'expiring'
  | 'hedge'
  | 'funding'
  | 'firstTrade'
export type IdeaKind = ReactiveKind | SetupId
export type IdeaBucket = 'reactive' | 'opportunity'

export interface TradeIdea {
  id: string
  kind: IdeaKind
  /** Reactive (adjust what you hold) or opportunity (open something new). */
  bucket: IdeaBucket
  /** The playbook family, for opportunities. */
  category?: OpportunityCategory
  asset: string
  symbol: string
  exchange: Exchange
  /** For the Order Form's prompt bar (English, like the palette's examples). */
  orderPrompt?: string
  /** A non-order action: open the hedge builder or the arb builder. */
  route?: 'hedge' | 'arb'
  /** The two legs to seed the Arb builder with. */
  arb?: {
    short: { accountId: string; symbol: string; exchange: Exchange }
    long: { accountId: string; symbol: string; exchange: Exchange }
  }
  /** Funding by venue (annualised percent), for an arbitrage idea's chart. */
  venueFunding?: { exchange: Exchange; symbol: string; apr: number }[]
  /** Why this coin: the user holds, trades, watches or is looking at it. */
  relevance?: RelevanceReason
  /** Values for the idea's title and reason messages. */
  params: Record<string, string | number>
  /** Price levels to draw on the idea's chart. */
  levels: { entry?: number; liquidation?: number; stop?: number; target?: number }
  /** The market order the idea amounts to, for a server preview of leverage and liquidation after. */
  preview?: {
    accountId: string
    side: OrderSide
    quantity: string
    denomination: Denomination
  }
  priority: number
}

export const MAX_IDEAS = 4
const TAKE_PROFIT_PERCENT = 40
const STOP_LOSS_PERCENT = -20
const STOP_DISTANCE = 0.05
const CONCENTRATION_SHARE = 0.7
const CONCENTRATION_MIN_USD = 1000
const HEAVY_FUNDING_APR = 15
const EXPIRY_WINDOW_MS = 24 * 3600_000

/** A size for an order prompt: whole units when large, trimmed decimals when small. */
export function formatAmount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0'
  if (value >= 100) return String(Math.round(value))
  if (value >= 1) return String(Number(value.toFixed(2)))
  return String(Number(value.toPrecision(3)))
}

/** A price for an order prompt, to about five significant digits. */
export function formatPrice(value: number): string {
  if (value >= 1000) return String(Math.round(value))
  return String(Number(value.toPrecision(5)))
}

function closeSide(risk: PositionRisk): 'sell' | 'buy' {
  return risk.side === 'long' ? 'sell' : 'buy'
}

function sizeOf(risk: PositionRisk): number {
  return Math.abs(Number(risk.position.quantity) || 0)
}

function levelsOf(risk: PositionRisk): TradeIdea['levels'] {
  const entry = Number(risk.position.avgPrice)
  const liquidation = Number(risk.position.liquidationPrice)
  return {
    entry: entry > 0 ? entry : undefined,
    liquidation: liquidation > 0 ? liquidation : undefined,
  }
}

function previewOf(risk: PositionRisk, quantity: number): TradeIdea['preview'] {
  return {
    accountId: risk.position.accountId,
    side: risk.side === 'long' ? OrderSide.ORDER_SIDE_SELL : OrderSide.ORDER_SIDE_BUY,
    quantity: formatAmount(quantity),
    denomination: risk.position.sizeDenomination ?? Denomination.DENOMINATION_UNSPECIFIED,
  }
}

function base(risk: PositionRisk): Pick<TradeIdea, 'asset' | 'symbol' | 'exchange'> {
  return {
    asset: baseAssetOf(risk.position.symbol),
    symbol: risk.position.symbol,
    exchange: risk.position.exchange,
  }
}

export interface IdeaInput {
  risks: readonly PositionRisk[]
  exposures: readonly AssetExposure[]
  /** Funding APR (percent) by perp symbol, from live tickers. */
  fundingApr?: Readonly<Record<string, number>>
  now?: number
}

export function buildIdeas({
  risks,
  exposures,
  fundingApr = {},
  now = Date.now(),
}: IdeaInput): TradeIdea[] {
  const ideas: TradeIdea[] = []

  for (const risk of risks) {
    const { position } = risk
    const size = sizeOf(risk)
    if (size === 0) continue
    const symbol = position.symbol
    const mark = Number(position.markPrice) || Number(position.avgPrice)

    if (risk.reasons.includes('nearLiquidation') || risk.reasons.includes('highLeverage')) {
      ideas.push({
        id: `reduce:${symbol}`,
        kind: 'reduce',
        bucket: 'reactive',
        ...base(risk),
        orderPrompt: `${closeSide(risk)} ${formatAmount(size / 2)} ${symbol} reduce only`,
        params: {
          symbol,
          leverage: risk.leverage ?? 0,
          distance:
            risk.liquidationDistance !== null
              ? Number((risk.liquidationDistance * 100).toFixed(1))
              : 0,
          hasLiquidation: risk.liquidationDistance !== null ? 'yes' : 'no',
        },
        levels: levelsOf(risk),
        preview: previewOf(risk, size / 2),
        priority: risk.level === 'danger' ? 100 : 90,
      })
      continue
    }

    const expiry = Number(position.expiry) || expiryOfSymbol(symbol)
    if (isOptionSymbol(symbol) && expiry && expiry > now && expiry - now <= EXPIRY_WINDOW_MS) {
      ideas.push({
        id: `expiring:${symbol}`,
        kind: 'expiring',
        bucket: 'reactive',
        ...base(risk),
        orderPrompt: `${closeSide(risk)} ${formatAmount(size)} ${symbol} reduce only`,
        params: { symbol, hours: Math.max(1, Math.round((expiry - now) / 3600_000)) },
        levels: levelsOf(risk),
        preview: previewOf(risk, size),
        priority: 80,
      })
      continue
    }

    if (risk.pnlPercent !== null && risk.pnlPercent >= TAKE_PROFIT_PERCENT) {
      ideas.push({
        id: `takeProfit:${symbol}`,
        kind: 'takeProfit',
        bucket: 'reactive',
        ...base(risk),
        orderPrompt: `${closeSide(risk)} ${formatAmount(size / 4)} ${symbol} reduce only`,
        params: { symbol, pnl: Math.round(risk.pnlPercent) },
        levels: levelsOf(risk),
        preview: previewOf(risk, size / 4),
        priority: 70,
      })
      continue
    }

    if (risk.pnlPercent !== null && risk.pnlPercent <= STOP_LOSS_PERCENT && mark > 0) {
      const levels = levelsOf(risk)
      let stop = risk.side === 'long' ? mark * (1 - STOP_DISTANCE) : mark * (1 + STOP_DISTANCE)
      // A stop past the liquidation price would never trigger; keep it on the safe side.
      if (levels.liquidation) {
        stop =
          risk.side === 'long'
            ? Math.max(stop, levels.liquidation * 1.01)
            : Math.min(stop, levels.liquidation * 0.99)
      }
      ideas.push({
        id: `stopLoss:${symbol}`,
        kind: 'stopLoss',
        bucket: 'reactive',
        ...base(risk),
        orderPrompt: `stop loss ${closeSide(risk)} ${formatAmount(size)} ${symbol} at ${formatPrice(stop)}`,
        params: { symbol, pnl: Math.round(risk.pnlPercent), stop: formatPrice(stop) },
        levels: { ...levels, stop },
        priority: 65,
      })
      continue
    }

    const apr = fundingApr[symbol]
    const paying =
      apr !== undefined &&
      ((risk.side === 'long' && apr >= HEAVY_FUNDING_APR) ||
        (risk.side === 'short' && apr <= -HEAVY_FUNDING_APR))
    if (paying) {
      ideas.push({
        id: `funding:${symbol}`,
        kind: 'funding',
        bucket: 'reactive',
        ...base(risk),
        orderPrompt: `${closeSide(risk)} ${formatAmount(size / 2)} ${symbol} reduce only`,
        params: { symbol, apr: Number(Math.abs(apr).toFixed(1)) },
        levels: levelsOf(risk),
        preview: previewOf(risk, size / 2),
        priority: 55,
      })
    }
  }

  const top = exposures[0]
  if (
    top &&
    exposures.filter((exposure) => exposure.positions.length > 0).length > 0 &&
    top.share >= CONCENTRATION_SHARE &&
    top.notionalUsd >= CONCENTRATION_MIN_USD &&
    top.perps[0]
  ) {
    ideas.push({
      id: `hedge:${top.asset}`,
      kind: 'hedge',
      bucket: 'reactive',
      asset: top.asset,
      symbol: top.perps[0].symbol,
      exchange: top.perps[0].exchange,
      route: 'hedge',
      params: { asset: top.asset, share: Math.round(top.share * 100) },
      levels: {},
      priority: 60,
    })
  }

  if (risks.length === 0) {
    ideas.push({
      id: 'firstTrade:BTC',
      kind: 'firstTrade',
      bucket: 'opportunity',
      asset: 'BTC',
      symbol: 'BTC-PERPETUAL',
      exchange: Exchange.EXCHANGE_DERIBIT,
      orderPrompt: 'buy $25 BTC at market',
      params: {},
      levels: {},
      priority: 10,
    })
  }

  // One idea per instrument (the hedge is about the whole book), the most pressing first.
  const seen = new Set<string>()
  return ideas
    .sort((a, b) => b.priority - a.priority)
    .filter((idea) => {
      const key = idea.kind === 'hedge' ? 'hedge' : idea.symbol
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, MAX_IDEAS)
}

/** The idea in one English line, for the AI brief's prompt. */
export function describeIdea(idea: TradeIdea): string {
  const p = idea.params
  switch (idea.kind) {
    case 'reduce':
      return `Cut ${idea.symbol} in half, reduce only (${p.leverage}x leverage, liquidation ${p.distance}% away): "${idea.orderPrompt}"`
    case 'takeProfit':
      return `Take a quarter of ${idea.symbol} off while it is up ${p.pnl}%: "${idea.orderPrompt}"`
    case 'stopLoss':
      return `Put a stop at ${p.stop} on ${idea.symbol}, down ${Math.abs(Number(p.pnl))}%: "${idea.orderPrompt}"`
    case 'funding':
      return `Halve ${idea.symbol}, which pays about ${p.apr}% a year in funding: "${idea.orderPrompt}"`
    case 'hedge':
      return `Delta-hedge the ${p.asset} exposure, ${p.share}% of the book, with OrderX's hedge builder`
    case 'expiring':
      return `Close or roll ${idea.symbol} before it expires in ${p.hours} hours: "${idea.orderPrompt}"`
    case 'firstTrade':
      return `A first small trade to learn the flow: "${idea.orderPrompt}"`
    case 'fundingArb':
      return `Funding arbitrage on ${p.asset}: short where funding is ${p.highApr}% a year, long where it is ${p.lowApr}%, collecting the ${p.spread}% spread with OrderX's arb builder`
    case 'breakout':
      return `Momentum long on ${idea.symbol}: up ${p.move}% in 15 minutes with open interest up ${p.oi}% in an hour and ${p.takers}% of volume taker buys. Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'breakdown':
      return `Momentum short on ${idea.symbol}: down ${p.move}% in 15 minutes with open interest up ${p.oi}% in an hour and ${p.takers}% of volume taker sells. Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'overextended':
      return `Mean-reversion ${p.side} on ${idea.symbol} after a ${p.move}% move ${p.direction} in 24 hours that has stopped extending. Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'crowdedFunding':
      return `Contrarian ${p.side} on ${idea.symbol}: ${p.crowd}s pay about ${p.apr}% a year in funding while open interest rose ${p.oi}% in a day. Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'etfFlows':
      return `${p.side === 'long' ? 'Long' : 'Short'} ${idea.symbol} on ${p.days} days of ETF ${p.direction}flows totalling $${Math.abs(Number(p.total)).toLocaleString('en-US')}. Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'unlockPressure':
      return `Short ${idea.symbol} into a token unlock in ${p.days} days worth $${Number(p.value).toLocaleString('en-US')} (${p.pct}% of market cap). Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
    case 'newsMove':
      return `${p.side === 'long' ? 'Long' : 'Short'} ${idea.symbol} with a news-driven move of ${p.move}% in 24 hours ("${p.title}"). Stop ${p.stop}, target ${p.target}: "${idea.orderPrompt}"`
  }
}
